import { useMutation } from '@tanstack/react-query';
import React, { forwardRef, useImperativeHandle, useState } from 'react';
import styled from 'styled-components';

import { Button, Icon, Input, Modal, ModalSize } from '@column/column-ui-kit';

import {
  type PendingPasskeyRegistration,
  beginPasskeyRegistration,
  finishPasskeyRegistration,
  getDefaultPasskeyName,
} from '~/lib/webauthn';
import { MFAStatus } from '~/repositories';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';

const NamePasskeyFields = styled.div`
  display: grid;
  grid-gap: 16px;
  padding-bottom: var(--page-margin);
`;

const NamePasskeyActions = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 20px;
`;

export interface SetupPasskeyFlowHandle {
  start: () => void;
}

interface SetupPasskeyFlowProps {
  notificationDisplay: string;
  onFailure: () => void;
  onSuccess: () => void;
}

export const SetupPasskeyFlow = forwardRef<SetupPasskeyFlowHandle, SetupPasskeyFlowProps>(
  ({ notificationDisplay, onFailure, onSuccess }, ref) => {
    const { addDangerNotification } = useNotificationStore();
    const firstName = useSessionStore((state) => state.currentUser?.firstName ?? '');
    const setMfaStatus = useSessionStore((state) => state.setMfaStatus);
    const [isOpen, setIsOpen] = useState(false);
    const [displayName, setDisplayName] = useState('');
    const [pendingRegistration, setPendingRegistration] = useState<PendingPasskeyRegistration>();

    const resetRegistration = () => {
      setIsOpen(false);
      setDisplayName('');
      setPendingRegistration(undefined);
    };

    const beginRegistration = useMutation({
      mutationFn: beginPasskeyRegistration,
      onSuccess: (registration) => {
        setPendingRegistration(registration);
        setDisplayName(getDefaultPasskeyName(firstName));
        setIsOpen(true);
      },
      onError: (error: unknown) => {
        onFailure();
        if (error instanceof DOMException && error.name === 'NotAllowedError') {
          return;
        }
        if (error instanceof DOMException && error.name === 'InvalidStateError') {
          addDangerNotification({
            content: 'A passkey has already been registered to this device',
            display: notificationDisplay,
          });
          return;
        }
        const content = error instanceof Error ? error.message : 'Failed to add passkey. Please try again.';
        addDangerNotification({ content, display: notificationDisplay });
      },
    });

    const finishRegistration = useMutation({
      mutationFn: ({ name, registration }: { name: string; registration: PendingPasskeyRegistration }) =>
        finishPasskeyRegistration({ displayName: name, registration }),
      onSuccess: () => {
        resetRegistration();
        onSuccess();
        setMfaStatus(MFAStatus.VERIFIED);
      },
      onError: (error: unknown) => {
        onFailure();
        const content = error instanceof Error ? error.message : 'Failed to add passkey. Please try again.';
        addDangerNotification({ content, display: notificationDisplay });
      },
    });

    useImperativeHandle(ref, () => ({
      start: () => {
        if (!beginRegistration.isPending) {
          beginRegistration.mutate();
        }
      },
    }));

    const handleCancel = () => {
      resetRegistration();
      onFailure();
    };

    return (
      <Modal isOpen={isOpen} onClose={handleCancel} size={ModalSize.Small}>
        <Modal.IconCircle type="primary">
          <Icon.Key />
        </Modal.IconCircle>

        <form
          onSubmit={(event) => {
            event.stopPropagation();
            event.preventDefault();
            const name = displayName.trim();
            if (name && pendingRegistration) {
              finishRegistration.mutate({ name, registration: pendingRegistration });
            }
          }}
        >
          <Modal.Content>
            <Modal.Headline fullWidth align="center">
              Name your passkey
            </Modal.Headline>
            <NamePasskeyFields>
              <Modal.Paragraph fullWidth align="center">
                Use a name that helps you recognize this key
              </Modal.Paragraph>
              <Input value={displayName} onChange={setDisplayName} placeholder="e.g. Work laptop" maxLength={127} />

              <NamePasskeyActions>
                <Button type="button" size="small" variant="secondary" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  size="small"
                  isDisabled={!displayName.trim() || !pendingRegistration}
                  isLoading={finishRegistration.isPending}
                >
                  Add passkey
                </Button>
              </NamePasskeyActions>
            </NamePasskeyFields>
          </Modal.Content>
        </form>
      </Modal>
    );
  }
);

SetupPasskeyFlow.displayName = 'SetupPasskeyFlow';
